import { DynamicConfig } from '@domain/configuration/types';
import { useGeneratedSalesPortalChatDynamicConfigQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-chat-dynamic-config.generated';
import { useMemo } from 'react';

interface ChatDynamicConfigLoaderParams {
  chatUUID?: string | null;
}

interface ChatDynamicConfigLoaderResult {
  chatDynamicConfig: DynamicConfig | null;
  error?: Error;
  loading: boolean;
}

/**
 * Loader to fetch a single chat's dynamic config, keyed on `chatUUID`.
 * Skipped when no `chatUUID` is provided.
 *
 * @param params - The loader parameters
 * @param params.chatUUID - The chat identifier (optional; skips when absent)
 * @returns An object containing the chat config, loading state, and error
 *
 * @example
 * ```typescript
 * const { chatDynamicConfig, loading } = useChatDynamicConfigLoader({
 *   chatUUID: 'chat-456',
 * });
 * ```
 */
export const useChatDynamicConfigLoader = ({
  chatUUID,
}: ChatDynamicConfigLoaderParams): ChatDynamicConfigLoaderResult => {
  const { data, error, loading } =
    useGeneratedSalesPortalChatDynamicConfigQuery({
      fetchPolicy: 'cache-and-network',
      skip: !chatUUID,
      variables: {
        chatUUID: chatUUID ?? '',
      },
    });

  // Cast required because GraphQL returns chat_dynamic_config as Record<string, unknown>
  const chatDynamicConfig = useMemo(
    () =>
      (data?.employee_chats?.[0]?.chat_dynamic_config ??
        null) as DynamicConfig | null,
    [data?.employee_chats],
  );

  return {
    chatDynamicConfig,
    error: error as Error | undefined,
    loading,
  };
};
